import { useEffect, useRef, useState } from "react";
import { AFTER_ALBUM_CHANGE, createAlbum, deleteAlbum, updateAlbum } from "@/api/mutations";
import { useAccao, texto } from "@/api/useAccao";
import { Confirmacao } from "@/components/ui/Confirmacao";
import { ALBUM_ICONS as ICONES_ALBUM, DEFAULT_ICON as ICONE_PADRAO } from "@historia/shared/icons";
import type { AlbumWithCount as AlbumComContagem } from "@historia/shared/types";

const campo =
  "flex-1 rounded-2xl border border-ouro/30 bg-creme-50 px-4 py-2.5 text-tinta outline-none transition-colors placeholder:text-tinta-700/40 focus:border-ouro focus:ring-2 focus:ring-ouro/30";

/** Grelha de emojis + input escondido `icone` com o escolhido. */
function SeletorIcone({ inicial }: { inicial?: string }) {
  const [icone, setIcone] = useState(inicial ?? ICONE_PADRAO);
  return (
    <div>
      <input type="hidden" name="icone" value={icone} />
      <div className="flex flex-wrap gap-1">
        {ICONES_ALBUM.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => setIcone(emoji)}
            aria-pressed={icone === emoji}
            aria-label={`Ícone ${emoji}`}
            className={`flex h-9 w-9 items-center justify-center rounded-xl text-lg transition-colors ${
              icone === emoji
                ? "bg-ouro/20 ring-2 ring-ouro"
                : "hover:bg-creme-200"
            }`}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
}

function BotaoSubmeter({ texto, pending = false }: { texto: string; pending?: boolean }) {
  return (
    <button
      type="submit"
      disabled={pending}
      className="rounded-2xl bg-ouro px-5 py-2.5 text-sm font-semibold text-creme-50 transition-colors hover:bg-ouro-600 disabled:opacity-60"
    >
      {pending ? "A guardar…" : texto}
    </button>
  );
}

function FormCriar() {
  // Success counter: changing the form's `key` on each create clears every
  // field, which is what re-rendering the server component used to do.
  const [criados, setCriados] = useState(0);

  const [estado, acao, aGuardar] = useAccao(
    (fd: FormData) => createAlbum({ nome: texto(fd, "nome"), icone: texto(fd, "icone") }),
    { invalidate: AFTER_ALBUM_CHANGE, onSuccess: () => setCriados((n) => n + 1) },
  );

  async function submeter(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await acao(new FormData(event.currentTarget));
  }

  return (
    <form onSubmit={submeter} key={criados} className="flex flex-col gap-3 rounded-2xl border border-ouro/15 bg-creme-50/70 p-4">
      <div className="flex gap-2">
        <input
          name="nome"
          required
          maxLength={60}
          placeholder="Novo álbum (ex.: Viagem a Itália)"
          className={campo}
        />
        <BotaoSubmeter texto="Criar" pending={aGuardar} />
      </div>
      <SeletorIcone />
      {estado.erro && <p className="text-sm text-tinta">{estado.erro}</p>}
    </form>
  );
}

function LinhaAlbum({ album }: { album: AlbumComContagem }) {
  const [aEditar, setAEditar] = useState(false);
  const [aApagar, setAApagar] = useState(false);

  const [, apagar] = useAccao((id: string) => deleteAlbum(id), {
    invalidate: AFTER_ALBUM_CHANGE,
  });
  const [estado, acao, aGuardar] = useAccao(
    (fd: FormData) =>
      updateAlbum({ id: album.id, nome: texto(fd, "nome"), icone: texto(fd, "icone") }),
    { invalidate: AFTER_ALBUM_CHANGE },
  );

  // On a successful rename, close the editor and refresh the list.
  useEffect(() => {
    if (aEditar && estado.ok) setAEditar(false);
  }, [estado.ok, aEditar]);

  async function submeter(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await acao(new FormData(event.currentTarget));
  }

  if (aEditar) {
    return (
      <li className="rounded-2xl border border-ouro/15 bg-creme-50/70 p-3">
        <form onSubmit={submeter} className="flex flex-col gap-3">
          <input type="hidden" name="id" value={album.id} />
          <div className="flex gap-2">
            <input
              name="nome"
              required
              maxLength={60}
              defaultValue={album.nome}
              className={campo}
              autoFocus
            />
            <BotaoSubmeter texto="Guardar" pending={aGuardar} />
            <button
              type="button"
              onClick={() => setAEditar(false)}
              className="rounded-2xl px-4 py-2.5 text-sm font-semibold text-tinta-700 hover:bg-creme-200"
            >
              Cancelar
            </button>
          </div>
          <SeletorIcone inicial={album.icone} />
          {estado.erro && <p className="text-sm text-tinta">{estado.erro}</p>}
        </form>
      </li>
    );
  }

  return (
    <li className="flex items-center justify-between gap-3 rounded-2xl border border-ouro/15 bg-creme-50/70 p-3">
      <span className="flex items-center gap-2 text-tinta">
        <span aria-hidden className="text-lg">{album.icone}</span>
        <span className="font-semibold">{album.nome}</span>
        <span className="rounded-full bg-creme-200/80 px-2 text-xs text-tinta-700/80">
          {album.total} {album.total === 1 ? "foto" : "fotos"}
        </span>
      </span>
      <span className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setAEditar(true)}
          className="rounded-full px-3 py-1.5 text-xs font-semibold text-ouro-600 transition-colors hover:bg-creme-200"
        >
          Editar
        </button>
        <button
          type="button"
          onClick={() => setAApagar(true)}
          className="rounded-full border border-blush/60 px-3 py-1.5 text-xs font-semibold text-tinta/80 transition-colors hover:bg-blush-300/30"
        >
          Apagar
        </button>
      </span>

      <Confirmacao
        aberto={aApagar}
        titulo="Apagar álbum?"
        mensagem={`Apagar o álbum "${album.nome}"? As fotos não são apagadas — voltam ao álbum geral.`}
        textoConfirmar="Apagar"
        destrutivo
        onCancelar={() => setAApagar(false)}
        onConfirmar={() => {
          setAApagar(false);
          void apagar(album.id);
        }}
      />
    </li>
  );
}

export function GestaoAlbuns({ albuns }: { albuns: AlbumComContagem[] }) {
  return (
    <div className="flex flex-col gap-6">
      <FormCriar />

      {albuns.length === 0 ? (
        <p className="text-center text-sm text-tinta-700/80">
          Ainda não há álbuns. Cria o primeiro acima.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {albuns.map((a) => (
            <LinhaAlbum key={a.id} album={a} />
          ))}
        </ul>
      )}
    </div>
  );
}
